import { ANALYTICS_SOURCE, ASK_ROVO_ACTION_SUBJECT_ID } from './analytics.ts';

export const ROVO_CHAT_ACTION_SUBJECT_ID = 'teamworkGraphRovoChat';

/** Which Ask Rovo control the user pressed to enter the chat. */
export type AskRovoTrigger = 'rowAction' | 'atAGlance' | 'shareContext' | 'twgVisualization';

/** Which Teamwork Graph surface is hosting the Rovo chat. */
export type RovoChatSurface = 'modal' | 'panel';

/** How the user left the Rovo chat. */
export type RovoChatCloseMethod = 'back' | 'modalClose' | 'panelClose';

/**
 * Fired the moment an Ask Rovo control is pressed, before the switch is attempted.
 * `trigger` names which of the three entries into Rovo it was; `product`/`resourceType`
 * are only carried by the per-link row action, whose click targets a specific connection.
 */
export const getAskRovoClickedEvent = (attributes: {
	trigger: AskRovoTrigger;
	product?: string;
	resourceType?: string;
}): {
	readonly type: 'sendUIEvent';
	readonly data: {
		readonly action: 'clicked';
		readonly actionSubject: 'button';
		readonly actionSubjectId: 'teamworkGraphAskRovo';
		readonly source: 'byline-connections';
		readonly attributes: { trigger: AskRovoTrigger; product?: string; resourceType?: string };
	};
} =>
	({
		type: 'sendUIEvent',
		data: {
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: ASK_ROVO_ACTION_SUBJECT_ID,
			source: ANALYTICS_SOURCE,
			attributes,
		},
	}) as const;

/**
 * Fired when a surface switches its body over to the Rovo chat, after an Ask Rovo control is used.
 * `trigger` names which control opened it — the same value as the paired `clicked` event.
 */
export const getRovoChatSwitchedEvent = (attributes: {
	surface: RovoChatSurface;
	trigger: AskRovoTrigger;
}): {
	readonly type: 'sendUIEvent';
	readonly data: {
		readonly action: 'switched';
		readonly actionSubject: 'rovoChat';
		readonly actionSubjectId: 'teamworkGraphRovoChat';
		readonly source: 'byline-connections';
		readonly attributes: { surface: RovoChatSurface; trigger: AskRovoTrigger };
	};
} =>
	({
		type: 'sendUIEvent',
		data: {
			action: 'switched',
			actionSubject: 'rovoChat',
			actionSubjectId: ROVO_CHAT_ACTION_SUBJECT_ID,
			source: ANALYTICS_SOURCE,
			attributes,
		},
	}) as const;

/**
 * Fired when the user leaves the Rovo chat — the back button, or closing the modal/panel from it.
 * `trigger` carries the control that opened the chat being left (the surface remembers it).
 */
export const getRovoChatClosedEvent = (attributes: {
	surface: RovoChatSurface;
	method: RovoChatCloseMethod;
	trigger?: AskRovoTrigger;
}): {
	readonly type: 'sendUIEvent';
	readonly data: {
		readonly action: 'closed';
		readonly actionSubject: 'rovoChat';
		readonly actionSubjectId: 'teamworkGraphRovoChat';
		readonly source: 'byline-connections';
		readonly attributes: {
			surface: RovoChatSurface;
			method: RovoChatCloseMethod;
			trigger?: AskRovoTrigger;
		};
	};
} =>
	({
		type: 'sendUIEvent',
		data: {
			action: 'closed',
			actionSubject: 'rovoChat',
			actionSubjectId: ROVO_CHAT_ACTION_SUBJECT_ID,
			source: ANALYTICS_SOURCE,
			attributes,
		},
	}) as const;
